import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

export type SessionCreationStepKey =
  | 'patient'
  | 'device'
  | 'sessionType'
  | 'configuration';

export type StepValidator<TValue = unknown> = (
  value: TValue | null
) => boolean | Promise<boolean>;

type StepState<TValue = unknown> = {
  value: TValue | null;
  isValid: boolean;
  error?: string | null;
};

type SessionCreationStepsState = Record<SessionCreationStepKey, StepState>;

type SessionCreationContextValue = {
  steps: SessionCreationStepsState;
  setStepValue: <TValue>(
    key: SessionCreationStepKey,
    value: TValue | null
  ) => void;
  setStepValidity: (key: SessionCreationStepKey, isValid: boolean) => void;
  setStepError: (key: SessionCreationStepKey, error: string | null) => void;
  registerStepValidator: <TValue>(
    key: SessionCreationStepKey,
    validator: StepValidator<TValue>
  ) => void;
  unregisterStepValidator: (key: SessionCreationStepKey) => void;
  validateStep: (key: SessionCreationStepKey) => Promise<boolean>;
  validateAll: () => Promise<{
    isValid: boolean;
    invalidSteps: SessionCreationStepKey[];
  }>;
};

const SessionCreationContext =
  createContext<SessionCreationContextValue | null>(null);

const createInitialStepsState = (): SessionCreationStepsState => ({
  patient: { value: null, isValid: false, error: null },
  device: { value: null, isValid: false, error: null },
  sessionType: { value: null, isValid: false, error: null },
  configuration: { value: null, isValid: false, error: null },
});

export function SessionCreationProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<SessionCreationStepsState>(
    createInitialStepsState
  );

  const validatorsRef = useRef<
    Partial<Record<SessionCreationStepKey, StepValidator>>
  >({});

  const stepsRef = useRef(steps);

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  const setStepValue = useCallback(
    <TValue,>(key: SessionCreationStepKey, value: TValue | null) => {
      setSteps(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          value,
        },
      }));
    },
    []
  );

  const setStepValidity = useCallback(
    (key: SessionCreationStepKey, isValid: boolean) => {
      setSteps(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          isValid,
        },
      }));
    },
    []
  );

  const setStepError = useCallback(
    (key: SessionCreationStepKey, error: string | null) => {
      setSteps(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          error,
        },
      }));
    },
    []
  );

  const registerStepValidator = useCallback(
    <TValue,>(
      key: SessionCreationStepKey,
      validator: StepValidator<TValue>
    ) => {
      validatorsRef.current[key] = validator as StepValidator;
    },
    []
  );

  const unregisterStepValidator = useCallback((key: SessionCreationStepKey) => {
    delete validatorsRef.current[key];
  }, []);

  const validateStep = useCallback(async (key: SessionCreationStepKey) => {
    const validator = validatorsRef.current[key];
    let isValid = false;

    if (validator) {
      try {
        const currentValue = stepsRef.current[key]?.value ?? null;
        isValid = Boolean(await validator(currentValue));
      } catch {
        isValid = false;
      }
    } else {
      isValid = Boolean(stepsRef.current[key]?.isValid);
    }

    setSteps(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        isValid,
      },
    }));

    return isValid;
  }, []);

  const validateAll = useCallback(async () => {
    const invalidSteps: SessionCreationStepKey[] = [];

    const stepKeys = Object.keys(stepsRef.current) as SessionCreationStepKey[];

    for (const key of stepKeys) {
      const isValid = await validateStep(key);
      if (!isValid) {
        invalidSteps.push(key);
      }
    }

    return {
      isValid: invalidSteps.length === 0,
      invalidSteps,
    };
  }, [validateStep]);

  const value = useMemo<SessionCreationContextValue>(
    () => ({
      steps,
      setStepValue,
      setStepValidity,
      setStepError,
      registerStepValidator,
      unregisterStepValidator,
      validateStep,
      validateAll,
    }),
    [
      steps,
      setStepValue,
      setStepValidity,
      setStepError,
      registerStepValidator,
      unregisterStepValidator,
      validateStep,
      validateAll,
    ]
  );

  return (
    <SessionCreationContext.Provider value={value}>
      {children}
    </SessionCreationContext.Provider>
  );
}

export function useSessionCreationContext() {
  const context = useContext(SessionCreationContext);

  if (!context) {
    throw new Error(
      'useSessionCreationContext must be used within a SessionCreationProvider'
    );
  }

  return context;
}

type StepControllerOptions<TValue> = {
  key: SessionCreationStepKey;
  defaultValue?: TValue | null;
  validator?: StepValidator<TValue>;
};

export function useStepController<TValue>({
  key,
  defaultValue,
  validator,
}: StepControllerOptions<TValue>) {
  const {
    steps,
    setStepValue,
    setStepValidity,
    registerStepValidator,
    unregisterStepValidator,
  } = useSessionCreationContext();

  const fallbackValidator = useCallback<StepValidator<TValue>>(
    candidate => Boolean(candidate),
    []
  );

  const activeValidator = validator ?? fallbackValidator;

  const evaluateValidity = useCallback(
    (candidate: TValue | null) => {
      void Promise.resolve(activeValidator(candidate)).then(result => {
        setStepValidity(key, Boolean(result));
      });
    },
    [activeValidator, key, setStepValidity]
  );

  const currentValue = (steps[key]?.value as TValue | null) ?? null;

  useEffect(() => {
    if (defaultValue == null || currentValue != null) {
      return;
    }
    setStepValue(key, defaultValue);
  }, [currentValue, defaultValue, key, setStepValue]);

  useEffect(() => {
    evaluateValidity(currentValue);
  }, [currentValue, evaluateValidity]);

  useEffect(() => {
    registerStepValidator(key, activeValidator);
    return () => {
      unregisterStepValidator(key);
    };
  }, [key, activeValidator, registerStepValidator, unregisterStepValidator]);

  const setValue = useCallback(
    (nextValue: TValue | null) => {
      setStepValue(key, nextValue);
    },
    [key, setStepValue]
  );

  return [currentValue, setValue] as const;
}
